// Walkable-surface map of the Capital (baked offline from the city geometry — see
// scripts/capital/bake_nav.py). A grid of cells; each cell holds zero or more FLOORS
// (height in metres + headroom), so a bridge deck and the ground beneath it are two
// separate layers. Only floors reachable from the spawn by stairs/ramps/gates survive the
// bake, so the cat can climb hills, cross bridges and enter the palace — but not hop onto
// rooftops or wall tops.
//
// All coordinates here are SOURCE METRES in capital-root space (x, z; heights are y).

export async function loadNav(url) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`nav ${res.status}`)
  const buf = await res.arrayBuffer()
  const dv = new DataView(buf)
  const hlen = dv.getUint32(0, true)
  const hdr = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, hlen)).trim())
  const cells = hdr.nx * hdr.nz
  let off = 4 + hlen
  const counts = new Uint8Array(buf, off, cells); off += cells
  off += (2 - (off % 2)) % 2
  const heights = new Int16Array(buf, off, hdr.layers); off += hdr.layers * 2
  const clear = new Uint8Array(buf, off, hdr.layers)
  return new NavGrid(hdr, counts, heights, clear)
}

const SIDE = [[1, 0], [-1, 0], [0, 1], [0, -1]]

export class NavGrid {
  constructor(hdr, counts, heights, clear) {
    this.cell = hdr.cell
    this.x0 = hdr.x0
    this.z0 = hdr.z0
    this.nx = hdr.nx
    this.nz = hdr.nz
    this.stepMax = hdr.step
    this.heights = heights
    this.clear = clear
    this.start = new Uint32Array(hdr.nx * hdr.nz + 1)
    for (let i = 0; i < counts.length; i++) this.start[i + 1] = this.start[i] + counts[i]
  }

  // Index of the floor in cell (cx,cz) closest to height y (within one step), with enough headroom.
  _layer(cx, cz, y, clrMin) {
    if (cx < 0 || cz < 0 || cx >= this.nx || cz >= this.nz) return -1
    const c = cz * this.nx + cx
    const need = clrMin * 10
    let best = -1
    let bestD = this.stepMax + 1e-6
    for (let i = this.start[c]; i < this.start[c + 1]; i++) {
      if (this.clear[i] < need) continue
      const d = Math.abs(this.heights[i] / 100 - y)
      if (d < bestD) { bestD = d; best = i }
    }
    return best
  }

  // Floor height at (x,z) near height y, smoothly blended across neighbouring cells; null if none.
  heightAt(x, z, y, clrMin) {
    const gx = (x - this.x0) / this.cell
    const gz = (z - this.z0) / this.cell
    const i0 = this._layer(Math.floor(gx), Math.floor(gz), y, clrMin)
    if (i0 < 0) return null
    const h0 = this.heights[i0] / 100
    const fx = gx - 0.5
    const fz = gz - 0.5
    const cx = Math.floor(fx)
    const cz = Math.floor(fz)
    const tx = fx - cx
    const tz = fz - cz
    let sum = 0
    let wsum = 0
    for (let dz = 0; dz < 2; dz++) {
      for (let dx = 0; dx < 2; dx++) {
        const i = this._layer(cx + dx, cz + dz, h0, clrMin)
        if (i < 0) continue
        const h = this.heights[i] / 100
        if (Math.abs(h - h0) > this.stepMax * 0.8) continue
        const w = (dx ? tx : 1 - tx) * (dz ? tz : 1 - tz)
        sum += h * w
        wsum += w
      }
    }
    return wsum > 0 ? sum / wsum : h0
  }

  // Can a body of `radius` stand at (x,z), arriving from height y? Returns the floor height there, or null.
  // The four side points must have a floor close to the centre's, so we don't clip walls or hang off ledges.
  step(x, z, y, radius, clrMin) {
    const h = this.heightAt(x, z, y, clrMin)
    if (h === null) return null
    for (const [dx, dz] of SIDE) {
      const hh = this.heightAt(x + dx * radius, z + dz * radius, h, clrMin)
      if (hh === null || Math.abs(hh - h) > this.stepMax) return null
    }
    return h
  }

  // Every floor height at a point (debug / teleports).
  floorsAt(x, z) {
    const cx = Math.floor((x - this.x0) / this.cell)
    const cz = Math.floor((z - this.z0) / this.cell)
    if (cx < 0 || cz < 0 || cx >= this.nx || cz >= this.nz) return []
    const c = cz * this.nx + cx
    const out = []
    for (let i = this.start[c]; i < this.start[c + 1]; i++) out.push(this.heights[i] / 100)
    return out
  }
}
